import type { ResolvedDateTimeFormat } from "@/shared/datetime";
import { Popover, RobotoButton } from "@/shared/foundation/primitives";

import { formatRange } from "./formatRange";
import type { ClusterClip } from "./layout";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import styles from "./TimelineTracks.module.css";
import { percent, trackVars } from "./trackStyle";

interface TrackClusterProps<T> {
  /** The items the badge stands for, and where it goes on its track. */
  clip: ClusterClip<T>;
  /** Reads the cluster's and its items' ranges, on the timeline's clock. */
  format: ResolvedDateTimeFormat;
  /** Whether its popover is open; the tracks keep one open at a time. */
  open: boolean;
  /** The popover opening or closing (a click on the badge, Escape, a press outside). */
  onOpenChange: (open: boolean) => void;
  /** An item picked from the popover; the timeline selects it. */
  onPick: (item: TimelineTrackItem<T>) => void;
  /** "Zoom to fit": the timeline narrows the window to the cluster's span. */
  onZoomToFit: () => void;
}

/** A cluster badge; clicking it opens `TrackClusterPopover`. */
export function TrackCluster<T>({
  clip,
  format,
  open,
  onOpenChange,
  onPick,
  onZoomToFit,
}: TrackClusterProps<T>) {
  const range = formatRange(clip, format);
  const count = clip.items.length;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger
        render={
          <button
            type="button"
            data-clip
            className={styles.cluster}
            style={{
              ...trackVars(clip.track),
              left: percent(clip.left),
              width: percent(clip.width),
            }}
            aria-label={`${count} items between ${range.start} and ${range.end}`}
          />
        }
      >
        {count}
      </Popover.Trigger>
      <TrackClusterPopover
        clip={clip}
        format={format}
        onPick={onPick}
        onZoomToFit={onZoomToFit}
      />
    </Popover>
  );
}

interface TrackClusterPopoverProps<T> {
  /** The items to list, each a button. */
  clip: ClusterClip<T>;
  /** Reads each item's range, on the timeline's clock. */
  format: ResolvedDateTimeFormat;
  onPick: (item: TimelineTrackItem<T>) => void;
  onZoomToFit: () => void;
}

/** The items a cluster stands for, each selectable, plus a zoom to the cluster's span. */
function TrackClusterPopover<T>({
  clip,
  format,
  onPick,
  onZoomToFit,
}: TrackClusterPopoverProps<T>) {
  return (
    <Popover.Content
      className={styles.clusterPopover}
      aria-label={`${clip.items.length} items in this cluster`}
    >
      <ul className={styles.clusterList}>
        {clip.items.map((item) => {
          const range = formatRange(item, format);
          return (
            <li key={item.id}>
              <button
                type="button"
                className={styles.clusterItem}
                onClick={() => onPick(item)}
              >
                <span className={styles.clusterItemLabel}>{item.label}</span>
                <span className={styles.clusterItemRange}>
                  {range.start} – {range.end}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className={styles.clusterActions}>
        <RobotoButton variant="secondary" size="sm" onClick={onZoomToFit}>
          Zoom to fit
        </RobotoButton>
      </div>
    </Popover.Content>
  );
}
